import { createContext, useCallback, useContext, useEffect, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../lib/api';
import { Icon } from './Icon';

export function Button({
  variant = 'primary',
  size = 'md',
  block,
  loading,
  children,
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  block?: boolean;
  loading?: boolean;
}) {
  return (
    <button
      {...rest}
      disabled={rest.disabled || loading}
      aria-busy={loading || undefined}
      className={`btn btn-${variant} btn-${size} ${block ? 'btn-block' : ''} ${className}`}
    >
      {loading ? <span className="spinner" aria-hidden /> : null}
      <span className="btn-label">{children}</span>
    </button>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="center-block" role="status">
      <span className="spinner spinner-lg" aria-hidden />
      {label ? <span className="muted">{label}</span> : null}
    </div>
  );
}

export function EmptyState({ icon = 'search', title, text, action }: { icon?: string; title: string; text?: string; action?: ReactNode }) {
  return (
    <div className="empty">
      <div className="empty-icon">
        <Icon name={icon} size={30} />
      </div>
      <h3>{title}</h3>
      {text ? <p className="muted">{text}</p> : null}
      {action}
    </div>
  );
}

/** Сообщение об ошибке API на языке пользователя по коду ошибки. */
export function errorMessage(t: (k: string, o?: Record<string, unknown>) => string, e: unknown): string {
  if (e instanceof ApiError) {
    const limit = (e.details as { limit?: number } | undefined)?.limit;
    return t(`errors.${e.code}`, { limit, defaultValue: t('errors.INTERNAL_ERROR') });
  }
  return t('errors.INTERNAL_ERROR');
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useTranslation();
  const requestId = error instanceof ApiError ? error.requestId : undefined;
  return (
    <div className="empty" role="alert">
      <div className="empty-icon empty-icon-error">
        <Icon name="close" size={28} />
      </div>
      <h3>{t('errors.title')}</h3>
      <p className="muted">{errorMessage(t, error)}</p>
      {requestId ? <p className="tiny muted">ID: {requestId}</p> : null}
      {onRetry ? (
        <Button variant="secondary" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      ) : null}
    </div>
  );
}

export function Sheet({ open, onClose, title, children, footer }: { open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    ref.current?.focus();
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" role="dialog" aria-modal="true" aria-label={title} ref={ref} tabIndex={-1} onClick={(e) => e.stopPropagation()}>
        <div className="sheet-header">
          <h2>{title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label={t('common.close')}>
            <Icon name="close" />
          </button>
        </div>
        <div className="sheet-body">{children}</div>
        {footer ? <div className="sheet-footer">{footer}</div> : null}
      </div>
    </div>
  );
}

// ---------------- уведомления (toast)

type Toast = { id: number; text: string; kind: 'info' | 'error' | 'success' };
const ToastContext = createContext<(text: string, kind?: Toast['kind']) => void>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((text: string, kind: Toast['kind'] = 'info') => {
    const id = Date.now() + Math.random();
    setToasts((ts) => [...ts.slice(-2), { id, text, kind }]);
    setTimeout(() => setToasts((ts) => ts.filter((x) => x.id !== id)), 3500);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="toasts" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.kind}`}>
            {t.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);

export function Chip({ active, children, onClick }: { active?: boolean; children: ReactNode; onClick?: () => void }) {
  return (
    <button type="button" className={`chip ${active ? 'chip-active' : ''}`} onClick={onClick} aria-pressed={active}>
      {children}
    </button>
  );
}

export function Field({ label, error, hint, children, htmlFor }: { label: string; error?: string; hint?: string; children: ReactNode; htmlFor?: string }) {
  return (
    <div className={`field ${error ? 'field-error' : ''}`}>
      <label className="field-label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {error ? (
        <div className="field-msg" role="alert">
          {error}
        </div>
      ) : hint ? (
        <div className="field-hint">{hint}</div>
      ) : null}
    </div>
  );
}

export function Toggle({ checked, onChange, label, description }: { checked: boolean; onChange: (v: boolean) => void; label: string; description?: string }) {
  return (
    <label className="toggle-row">
      <span>
        <span className="toggle-label">{label}</span>
        {description ? <span className="toggle-desc">{description}</span> : null}
      </span>
      <input type="checkbox" role="switch" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="toggle" aria-hidden />
    </label>
  );
}

export function ListRow({ icon, label, value, onClick, danger }: { icon?: string; label: string; value?: ReactNode; onClick?: () => void; danger?: boolean }) {
  return (
    <button type="button" className={`list-row ${danger ? 'list-row-danger' : ''}`} onClick={onClick}>
      {icon ? <Icon name={icon} /> : null}
      <span className="list-row-label">{label}</span>
      {value !== undefined ? <span className="list-row-value">{value}</span> : null}
      <Icon name="chevron" size={18} className="muted" />
    </button>
  );
}

/** Дата в формате ДД.ММ.ГГГГ (принят в Узбекистане и для всех трёх языков), независимо от языка телефона. */
export function formatDate(iso: string | Date): string {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
}

/** Относительная дата на языке интерфейса. Даты приходят в UTC, отображаются в локальном времени устройства. */
export function useRelativeDate() {
  const { t, i18n } = useTranslation();
  return (iso: string | null | undefined) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const days = Math.round((startOfDay(now) - startOfDay(d)) / 86400_000);
    const time = d.toLocaleTimeString(i18n.language === 'ru' ? 'ru-RU' : 'uz-UZ', { hour: '2-digit', minute: '2-digit' });
    if (days === 0) return t('date.today', { time });
    if (days === 1) return t('date.yesterday', { time });
    const months = t('date.months', { returnObjects: true }) as unknown as string[];
    return `${d.getDate()} ${months[d.getMonth()] ?? ''}${d.getFullYear() !== now.getFullYear() ? ` ${d.getFullYear()}` : ''}`;
  };
}
